import type { EnvironmentProviders, Provider } from '@angular/core';
import { environment } from './environments/environment';
import { setChatThemeModule } from './app/core/federation/chat-theme-bridge';
import {
  rejectChatFederationProviders,
  resolveChatFederationProviders,
} from './app/core/federation/chat-federation-bridge';

/**
 * Presupuesto de tiempo (ms) para el registro del remoto chat-mfe. Ya NO
 * bloquea el bootstrap de Angular (ver initHostFederation/loadChatMfeInBackground
 * más abajo) — solo determina cuánto tarda la feature de chat en darse por
 * vencida en el peor caso si el remoto no responde a tiempo.
 */
const CHAT_MFE_REGISTER_TIMEOUT_MS = 1500;

/**
 * Fase 1 — host-only, SIN remotos ni timeout, y NO puede saltarse. `main.ts`
 * la espera (`await`) antes de arrancar Angular.
 *
 * initFederation() solo inyecta el import map al DOM una vez, al final,
 * después de esperar a TODOS los remotos configurados (Promise.all interno).
 * Si le pasáramos 'chat-mfe' aquí y ese fetch se cuelga, el import map del
 * PROPIO host (@angular/core, rxjs, etc, externalizados por `shareAll()`)
 * nunca se inyecta — rompiendo el arranque de toda la app, no solo del chat.
 * Llamando con {} (sin remotos), el import map del host se inyecta sin
 * depender de ningún remoto externo. Esta llamada es same-origin y no se
 * acota con timeout a propósito: si esto no resuelve, el sitio completo ya
 * está caído, no es un riesgo nuevo introducido por federation.
 */
export async function initHostFederation(): Promise<void> {
  if (!environment.useFederation || !environment.remoteChatMfeUrl) return;

  const preconnect = document.createElement('link');
  preconnect.rel = 'preconnect';
  preconnect.href = environment.remoteChatMfeUrl;
  document.head.appendChild(preconnect);

  try {
    const { initFederation } = await import('@angular-architects/native-federation');
    await initFederation({});
  } catch (err) {
    console.error('[Federation] host init failed, continuing without federation', err);
  }
}

/**
 * Fase 2 — registro del remoto chat-mfe + carga de ThemeInit/ChatProviders/
 * GlobalStyles. Fire-and-forget: `main.ts` la dispara sin esperarla, así que
 * el bootstrap de Angular nunca depende de chat-mfe. El resultado alimenta
 * `chat-federation-bridge.ts`, que `ChatFederationReadyService` traduce a
 * `chatFederationReady$` para los 4 host components de chat.
 */
export function loadChatMfeInBackground(): void {
  void runChatMfePhase2();
}

async function runChatMfePhase2(): Promise<void> {
  if (!environment.useFederation || !environment.remoteChatMfeUrl) {
    rejectChatFederationProviders();
    return;
  }

  try {
    const providers = await loadChatMfeAssets();
    if (providers.length === 0) {
      rejectChatFederationProviders();
      return;
    }
    resolveChatFederationProviders(providers);
  } catch (err) {
    console.error('[Federation] chat-mfe background load failed, continuing without it', err);
    rejectChatFederationProviders();
  }
}

/**
 * Espera a `work` hasta `ms` milisegundos; si no terminó a tiempo, resuelve
 * de todas formas y deja que `work` siga corriendo en segundo plano (no hay
 * forma de cancelar el fetch subyacente — la librería no expone AbortSignal).
 */
function raceWithTimeout<T>(work: Promise<T>, ms: number): Promise<void> {
  return new Promise<void>(resolve => {
    const timer = setTimeout(() => {
      console.warn(`[Federation] chat-mfe registration exceeded ${ms}ms budget, giving up for this session`);
      resolve();
    }, ms);

    const stop = () => {
      clearTimeout(timer);
      resolve();
    };
    work.then(stop, stop);
  });
}

async function loadChatMfeAssets(): Promise<(Provider | EnvironmentProviders)[]> {
  const extraProviders: (Provider | EnvironmentProviders)[] = [];
  const { loadRemoteModule, fetchAndRegisterRemote } = await import('@angular-architects/native-federation');

  // fetchAndRegisterRemote (no una segunda llamada a initFederation): volver a
  // llamar initFederation() reinyecta el import map del host una segunda vez, y
  // es-module-shims rechaza ese "override". fetchAndRegisterRemote registra el
  // remoto y devuelve solo SU fragmento de import map, agregado de forma
  // incremental vía la API pública de es-module-shims.
  const remoteReady = fetchAndRegisterRemote(`${environment.remoteChatMfeUrl}/remoteEntry.json`, 'chat-mfe')
    .then(remoteImportMap => {
      if (remoteImportMap) {
        (globalThis as any).importShim?.addImportMap?.(remoteImportMap);
      }
    })
    .catch(err => {
      console.error('[Federation] could not register chat-mfe remote', err);
    });

  await raceWithTimeout(remoteReady, CHAT_MFE_REGISTER_TIMEOUT_MS);

  // Los 3 no dependen entre sí, así que van en paralelo. Si el registro del
  // remoto (arriba) no llegó a tiempo, cada loadRemoteModule falla limpio
  // con "unknown remote chat-mfe", atrapado por su propio try/catch.
  const loadTheme = async (): Promise<void> => {
    try {
      const themeMod = await loadRemoteModule({ remoteName: 'chat-mfe', exposedModule: './ThemeInit' });
      (themeMod as any).initChatTheme?.();
      setChatThemeModule(themeMod as any);
    } catch (err) {
      console.warn('[Federation] could not init chat theme', err);
    }
  };

  const loadProviders = async (): Promise<void> => {
    try {
      const providersMod = await loadRemoteModule({ remoteName: 'chat-mfe', exposedModule: './ChatProviders' });
      const provideChat = (providersMod as any).provideChat;
      if (typeof provideChat === 'function') {
        extraProviders.push(provideChat());
      }
    } catch (err) {
      console.error('[Federation] could not load ChatProviders', err);
    }
  };

  const loadStyles = async (): Promise<void> => {
    try {
      const stylesMod = await loadRemoteModule({ remoteName: 'chat-mfe', exposedModule: './GlobalStyles' });
      (stylesMod as any).initChatGlobalStyles?.();
    } catch (err) {
      console.warn('[Federation] could not init chat global styles', err);
    }
  };

  await Promise.allSettled([loadTheme(), loadProviders(), loadStyles()]);
  return extraProviders;
}
